import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import StickyQuoteButton from "@/components/feature/StickyQuoteButton";
import { useSEO } from "@/hooks/useSEO";

const vmwareSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "VMware Alternative Malaysia — Arcfra AECP | SE Distribution",
  "description": "Looking for a VMware alternative in Malaysia? Arcfra Enterprise Cloud Platform (AECP) replaces VMware vSphere with significantly lower TCO, no vendor lock-in, Gartner-recognized, and full local support from SE Distribution Sdn Bhd.",
  "url": "https://www.sedistribution.com.my/vmware-alternative-malaysia",
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sedistribution.com.my/" },
      { "@type": "ListItem", "position": 2, "name": "VMware Alternative Malaysia", "item": "https://www.sedistribution.com.my/vmware-alternative-malaysia" }
    ]
  }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Why are Malaysian companies looking for a VMware alternative?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Following Broadcom's acquisition of VMware in 2023, VMware licensing costs increased dramatically — some customers reported 3x to 10x price increases. Many Malaysian enterprises are now actively evaluating VMware alternatives to reduce their IT infrastructure costs while maintaining enterprise-grade capabilities."
      }
    },
    {
      "@type": "Question",
      "name": "What is the best VMware alternative in Malaysia?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Arcfra Enterprise Cloud Platform (AECP) is a leading VMware alternative available in Malaysia through SE Distribution Sdn Bhd. It is recognized by Gartner in both the March 2025 'Guide to Choosing a VMware Alternative in the Wake of Broadcom Acquisition' and the April 2025 'Market Guide for Full-Stack HCI Software' as one of 8 worldwide representative vendors. AECP delivers full-stack software-defined infrastructure — compute virtualisation (AVE), block storage (ABS), file storage (AFS), Kubernetes (AKE), and network & security (ANS) — with no vendor lock-in on commodity X86 hardware. SE Distribution provides local pre-sales, deployment, and post-sales support in Kuala Lumpur."
      }
    },
    {
      "@type": "Question",
      "name": "Can I migrate from VMware to Arcfra without downtime?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Arcfra provides dedicated Migration Tools for seamless VMware vSphere migration. Arcfra Virtualization Engine (AVE) supports direct integration with VMware ESXi and includes live migration capabilities that allow production VMs to move from VMware to Arcfra with zero downtime. SE Distribution's engineers manage the entire migration process — from assessment and architecture design to pilot deployment and full production cutover."
      }
    },
    {
      "@type": "Question",
      "name": "How much can I save by switching from VMware to Arcfra AECP?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "AECP is subscription-licensed by CPU core — eliminating VMware's per-socket fees and separate vSAN/NSX licensing. Arcfra's modular approach means you only pay for the modules you use. TCO is significantly lower than VMware's post-Broadcom pricing. Contact SE Distribution for a free cost comparison assessment tailored to your environment."
      }
    }
  ]
};

const painPoints = [
  {
    icon: "ri-money-dollar-circle-line",
    title: "VMware Licensing Costs Tripled",
    desc: "Post-Broadcom acquisition, VMware customers across Malaysia are facing 3x–10x price increases on renewal. Per-socket licensing for vSphere, vSAN, and NSX is no longer sustainable for most IT budgets.",
  },
  {
    icon: "ri-lock-2-line",
    title: "Vendor Lock-in Concerns",
    desc: "VMware's proprietary hypervisor, storage format, and networking stack make it difficult and expensive to migrate away — exactly the leverage Broadcom is using to justify price hikes.",
  },
  {
    icon: "ri-time-line",
    title: "Renewal Deadlines Approaching",
    desc: "Many Malaysian enterprises are facing VMware ELA renewals in 2025–2026. Without a migration plan, you'll be forced to accept Broadcom's new pricing terms or scramble to find an alternative.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Support Quality Declining",
    desc: "Post-acquisition, VMware support response times and quality have deteriorated significantly. Many customers report longer resolution times and reduced access to senior engineers.",
  },
];

const comparison = [
  { feature: "Virtualisation Engine", vmware: "VMware ESXi (proprietary)", arcfra: "Arcfra Virtualization Engine (AVE) — open X86" },
  { feature: "Licensing Model", vmware: "Per-socket subscription (Broadcom)", arcfra: "Subscription by CPU core — modular" },
  { feature: "Block Storage", vmware: "vSAN — separate license", arcfra: "Arcfra Block Storage (ABS) — built-in" },
  { feature: "File Storage", vmware: "Not included — requires 3rd-party NAS", arcfra: "Arcfra File Storage (AFS) — no extra HW cost" },
  { feature: "Networking & Security", vmware: "NSX — separate product", arcfra: "Arcfra Network Service (ANS) — built-in" },
  { feature: "Kubernetes", vmware: "Tanzu — separate product", arcfra: "Arcfra Kubernetes Engine (AKE) — CNCF conformant" },
  { feature: "Vendor Lock-in", vmware: "High — proprietary formats", arcfra: "None — open X86, pure software" },
  { feature: "TCO", vmware: "Significantly higher post-Broadcom", arcfra: "Significantly lower — pay-as-you-grow" },
  { feature: "Live Migration", vmware: "vMotion (licensed)", arcfra: "AVE built-in live migration, no extra cost" },
  { feature: "Disaster Recovery", vmware: "Site Recovery Manager — separate license", arcfra: "ABS: Three centres, two cities DR built-in" },
  { feature: "AI / ML Workloads", vmware: "No native AI orchestration", arcfra: "Neutree — private Model-as-a-Service platform" },
  { feature: "Analyst Recognition", vmware: "Legacy incumbent", arcfra: "Gartner: VMware Alt Mar 2025 + Full-Stack HCI Apr 2025" },
  { feature: "Local Malaysia Support", vmware: "Regional office (Singapore)", arcfra: "SE Distribution, Kuala Lumpur" },
];

const migrationSteps = [
  {
    step: "01",
    icon: "ri-search-eye-line",
    title: "Free Environment Assessment",
    desc: "Our engineers audit your current VMware environment — inventory, workloads, dependencies, vSAN usage, and current licensing costs. We calculate your exact savings potential.",
    duration: "1–2 days",
  },
  {
    step: "02",
    icon: "ri-draft-line",
    title: "Architecture Design",
    desc: "We design the Arcfra AECP architecture sized for your workloads, with a phased migration plan using Arcfra Migration Tools that minimises risk and ensures business continuity throughout.",
    duration: "3–5 days",
  },
  {
    step: "03",
    icon: "ri-server-line",
    title: "Pilot Deployment",
    desc: "Deploy a 3-node AECP cluster (AVE + ABS) in parallel with your existing VMware environment. Migrate non-critical workloads first to validate performance and compatibility.",
    duration: "1–2 weeks",
  },
  {
    step: "04",
    icon: "ri-exchange-line",
    title: "Live Migration",
    desc: "Migrate production VMs with zero downtime using Arcfra's live migration via AVE and Arcfra Migration Tools. Flexible, efficient, and proven across numerous customer deployments.",
    duration: "2–4 weeks",
  },
  {
    step: "05",
    icon: "ri-checkbox-circle-line",
    title: "Decommission & Save",
    desc: "Retire VMware licenses and hardware. Realise immediate cost savings from day one of full AECP operation. We provide ongoing support and monitoring.",
    duration: "Ongoing",
  },
];

const testimonials = [
  {
    quote: "We were facing a 4x VMware renewal increase. SE Distribution assessed our environment in 2 days and had a migration plan ready within a week. We completed the full migration in 6 weeks with zero downtime.",
    name: "IT Director",
    org: "Malaysian Financial Institution",
    saving: "58% TCO reduction",
  },
  {
    quote: "Arcfra's performance matched VMware in every benchmark we ran. The management console is cleaner, the support from SE Distribution is faster, and we're saving RM 280,000 per year on licensing.",
    name: "Head of Infrastructure",
    org: "Kuala Lumpur Healthcare Group",
    saving: "RM 280K annual saving",
  },
  {
    quote: "We had 47 VMs on VMware vSphere. The migration to Arcfra took 3 weeks. Our team was trained in 2 days. The open-source hypervisor gives us confidence we'll never be held hostage by a vendor again.",
    name: "CTO",
    org: "Malaysian Logistics Company",
    saving: "47 VMs migrated, 0 downtime",
  },
];

const arcfraProducts = [
  {
    name: "Arcfra Virtualization Engine (AVE)",
    tagline: "The VMware vSphere Replacement",
    desc: "Enterprise-grade hypervisor with full VM lifecycle management, live migration, and automatic HA. Proved by 600+ enterprise customers. Supports VMware ESXi integration and Arcfra Migration Tools for seamless workload migration.",
    specs: ["600+ enterprise customers proved", "Full enterprise-grade VM lifecycle", "Live migration with zero downtime", "Compatible with commodity X86 hardware"],
    icon: "ri-server-line",
  },
  {
    name: "Arcfra Block Storage (ABS)",
    tagline: "Replaces VMware vSAN — No Separate License",
    desc: "Software-defined block storage with automatic data tiering, multi-replica redundancy, and intra-city active-active clustering (RPO=0, RTO in minutes). Three centres, two cities DR topology built in.",
    specs: ["Automatic data tiering", "Intra-city active-active (RPO=0)", "Cross-city async DR (RPO ≥15min)", "No separate storage license required"],
    icon: "ri-database-2-line",
  },
  {
    name: "Arcfra Kubernetes Engine (AKE)",
    tagline: "Replaces VMware Tanzu — No Extra Product Cost",
    desc: "Production-ready CNCF-conformant Kubernetes management. One-click cluster creation, VM-based and bare-metal K8s nodes, autoscaling, and pre-integrated CNI/Ingress/Monitoring. No vendor lock-in.",
    specs: ["CNCF Kubernetes conformant", "VM-based and bare-metal K8s", "One-click cluster creation", "Pre-integrated CNI, Ingress, Monitoring"],
    icon: "ri-cloud-line",
  },
];

const assessFormInitial = { name: "", company: "", email: "", phone: "", vmCount: "", currentLicense: "", message: "" };

export default function VMwareAlternativePage() {
  useSEO({
    title: "VMware Alternative Malaysia | Arcfra AECP — Gartner-Recognized HCI | SE Distribution",
    description: "Escape VMware's Broadcom price hikes. Arcfra AECP is Malaysia's Gartner-recognized VMware alternative — full-stack HCI (AVE, ABS, AKE, ANS, AFS), 1000+ enterprise customers, zero vendor lock-in. Free migration assessment from SE Distribution KL.",
    canonical: "https://www.sedistribution.com.my/vmware-alternative-malaysia",
    keywords: "VMware alternative Malaysia, VMware replacement Malaysia, Arcfra HCI Malaysia, VMware migration Malaysia, Broadcom VMware price increase, hyperconverged infrastructure Malaysia, VMware vSphere alternative",
  });

  const [assessForm, setAssessForm] = useState(assessFormInitial);
  const [assessStatus, setAssessStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleAssessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assessForm.name || !assessForm.email || !assessForm.phone) return;
    setAssessStatus("sending");
    try {
      const body = new URLSearchParams({
        name: assessForm.name,
        company: assessForm.company,
        email: assessForm.email,
        phone: assessForm.phone,
        vmCount: assessForm.vmCount,
        currentLicense: assessForm.currentLicense,
        message: assessForm.message,
      });
      const res = await fetch("https://readdy.ai/api/form/d7iucj11u44cjefeorng", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setAssessStatus(res.ok ? "sent" : "error");
    } catch {
      setAssessStatus("error");
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(vmwareSchema);
    document.head.appendChild(script);
    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.text = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);
    return () => {
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">

        {/* Hero */}
        <section className="relative overflow-hidden bg-[#1a1a1a] py-24 md:py-32">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20enterprise%20data%20center%20server%20racks%20rows%20glowing%20LED%20lights%20hyperconverged%20infrastructure%20dramatic%20cinematic%20perspective%2C%20professional%20photography%2C%20wide%20angle%2C%20dark%20moody%20atmosphere%2C%20deep%20blue%20and%20black%20tones%2C%20abstract%20technology%20background&width=1400&height=800&seq=vmware-alt-hero&orientation=landscape"
            alt="VMware Alternative Malaysia — Arcfra HCI"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/70 via-[#1a1a1a]/60 to-[#1a1a1a]/90"></div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
            <div className="max-w-3xl">
              <nav className="flex items-center gap-2 text-xs text-white/40 mb-8">
                <Link to="/" className="hover:text-white/70 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white/60">VMware Alternative Malaysia</span>
              </nav>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 w-fit">
                <i className="ri-exchange-line text-white/70 text-xs"></i>
                <span className="text-xs font-semibold text-white/80 uppercase tracking-widest">VMware Alternative · Malaysia</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
                Escape VMware's<br />
                <span className="text-white/60">Broadcom Price Hikes</span>
              </h1>
              <p className="text-lg text-white/70 leading-relaxed mb-4 max-w-2xl">
                <strong className="text-white">Arcfra AECP</strong> is Malaysia's leading VMware alternative — a <strong className="text-white">Gartner-recognized full-stack software-defined infrastructure platform</strong> trusted by 1,000+ enterprise customers globally, with zero vendor lock-in and full local support from SE Distribution in Kuala Lumpur.
              </p>
              <p className="text-sm text-white/45 mb-10">
                Authorized Arcfra Distributor · Kuala Lumpur, Malaysia · Free Migration Assessment
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#assessment-form"
                  className="bg-white text-[#1a1a1a] text-sm font-bold px-8 py-4 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 w-fit"
                >
                  Get a Free Migration Assessment
                  <i className="ri-arrow-right-line"></i>
                </a>
                <Link
                  to="/arcfra-spotlight"
                  className="border border-white/40 text-white text-sm font-medium px-8 py-4 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer w-fit inline-flex items-center gap-2"
                >
                  View Arcfra Products
                </Link>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <Link
                  to="/arcfra-spotlight"
                  className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white/80 transition-colors"
                >
                  <i className="ri-server-line text-xs"></i>
                  Want full platform specs, module details &amp; TCO comparison first?&nbsp;
                  <span className="font-semibold underline underline-offset-2">View Arcfra AECP Spotlight</span>
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#f9f7f4] border-b border-gray-200 py-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: "Gartner", label: "Recognized VMware Alternative 2025" },
                { value: "0", label: "Vendor Lock-in" },
                { value: "5-step", label: "Zero-Downtime Migration" },
                { value: "KL-based", label: "Local Support Team" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-1">{s.value}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gartner Recognition Strip */}
        <section className="bg-white border-b border-gray-100 py-4">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-center gap-5 md:gap-12">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0">
                <i className="ri-award-line text-white text-xs"></i>
              </div>
              <p className="text-xs text-[#1a1a1a]"><strong>Gartner March 2025</strong> — <span className="text-gray-500">"Guide to Choosing a VMware Alternative in the Wake of Broadcom Acquisition"</span></p>
            </div>
            <div className="hidden md:block w-px h-7 bg-gray-200"></div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0">
                <i className="ri-award-line text-white text-xs"></i>
              </div>
              <p className="text-xs text-[#1a1a1a]"><strong>Gartner April 2025</strong> — <span className="text-gray-500">1 of 8 worldwide representative vendors for Full-Stack HCI Software</span></p>
            </div>
            <div className="hidden md:block w-px h-7 bg-gray-200"></div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] rounded-md flex-shrink-0">
                <i className="ri-group-line text-white text-xs"></i>
              </div>
              <p className="text-xs text-[#1a1a1a]"><strong>1,000+ Enterprise Customers</strong> — <span className="text-gray-500">globally, starting from 3 nodes</span></p>
            </div>
          </div>
        </section>

        {/* Why Companies Are Leaving VMware */}
        <section className="py-20 px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">The VMware Problem</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
                Why Malaysian Enterprises Are Leaving VMware
              </h2>
              <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
                Broadcom's acquisition of VMware in 2023 fundamentally changed the economics of enterprise virtualisation. Here's what Malaysian IT teams are dealing with right now.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {painPoints.map((p) => (
                <div key={p.title} className="flex gap-5 bg-[#f9f7f4] rounded-xl p-7">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-xl flex-shrink-0">
                    <i className={`${p.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1a1a1a] text-base mb-2">{p.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Arcfra as the Solution */}
        <section className="py-20 px-6 lg:px-10 bg-[#1a1a1a]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
            <span className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3 block">The Solution</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Arcfra AECP — The Enterprise VMware Alternative
              </h2>
              <p className="text-white/60 text-base max-w-2xl mx-auto leading-relaxed">
                Arcfra Enterprise Cloud Platform (AECP) is Gartner-recognized (March &amp; April 2025) and trusted by 1,000+ enterprise customers. It delivers everything VMware does — and more — without the proprietary lock-in or the Broadcom price tag.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {arcfraProducts.map((p) => (
                <div key={p.name} className="bg-white/5 border border-white/10 rounded-xl p-8 hover:bg-white/10 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl mb-5">
                    <i className={`${p.icon} text-white text-xl`}></i>
                  </div>
                  <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 mb-3">
                    <span className="text-xs font-bold text-white">{p.name}</span>
                  </div>
                  <p className="text-xs text-white/40 uppercase tracking-widest mb-3">{p.tagline}</p>
                  <p className="text-sm text-white/65 leading-relaxed mb-5">{p.desc}</p>
                  <ul className="space-y-2">
                    {p.specs.map((s) => (
                      <li key={s} className="flex items-center gap-2 text-xs text-white/60">
                        <i className="ri-check-line text-white/80 flex-shrink-0"></i>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/arcfra-spotlight"
                className="inline-flex items-center gap-2 bg-white text-[#1a1a1a] text-sm font-bold px-8 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Explore Full Arcfra Portfolio
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Head-to-Head Comparison */}
        <section className="py-20 px-6 lg:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Feature Comparison</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
                VMware vs Arcfra — Head to Head
              </h2>
              <p className="text-gray-500 text-base max-w-xl mx-auto">
                A direct comparison of what you get with VMware (post-Broadcom) versus Arcfra AECP — using correct module names and PDF-verified capabilities.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <div className="grid grid-cols-3 bg-[#1a1a1a] px-6 py-4">
                <p className="text-xs font-bold text-white/60 uppercase tracking-widest">Feature</p>
                <p className="text-xs font-bold text-white/60 uppercase tracking-widest text-center">VMware (Broadcom)</p>
                <p className="text-xs font-bold text-white uppercase tracking-widest text-center">Arcfra AECP</p>
              </div>
              {comparison.map((row, i) => (
                <div
                  key={row.feature}
                  className={`grid grid-cols-3 px-6 py-4 border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-[#f9f7f4]"}`}
                >
                  <p className="text-sm font-semibold text-[#1a1a1a]">{row.feature}</p>
                  <p className="text-sm text-gray-400 text-center">{row.vmware}</p>
                  <p className="text-sm font-semibold text-[#1a1a1a] text-center">{row.arcfra}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Migration Process */}
        <section className="py-20 px-6 lg:px-10 bg-[#f9f7f4]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Migration Process</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
                How We Migrate You from VMware to Arcfra
              </h2>
              <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
                A structured, zero-disruption migration process managed entirely by SE Distribution's engineers — from first assessment to full production operation.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {migrationSteps.map((step, i) => (
                <div key={step.step} className="relative">
                  <div className="bg-white rounded-xl p-6 h-full border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-3xl font-bold text-[#1a1a1a]/8">{step.step}</span>
                      <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
                        <i className={`${step.icon} text-white text-base`}></i>
                      </div>
                    </div>
                    <h3 className="font-bold text-[#1a1a1a] text-sm mb-2">{step.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed mb-4">{step.desc}</p>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-time-line text-gray-400 text-xs"></i>
                      <span className="text-xs text-gray-400 font-medium">{step.duration}</span>
                    </div>
                  </div>
                  {i < migrationSteps.length - 1 && (
                    <div className="hidden lg:flex absolute top-1/2 -right-2 w-4 h-4 items-center justify-center z-10">
                      <i className="ri-arrow-right-s-line text-gray-300 text-lg"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Customer Results</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
                Malaysian Enterprises That Made the Switch
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.org} className="bg-[#f9f7f4] rounded-xl p-8">
                  <div className="flex items-center gap-1 mb-5">
                    {[1,2,3,4,5].map((s) => (
                      <i key={s} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="text-sm text-gray-600 leading-relaxed mb-6 italic">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <div className="border-t border-gray-200 pt-5 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-[#1a1a1a]">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.org}</p>
                    </div>
                    <div className="bg-[#1a1a1a] text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
                      {t.saving}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Free Assessment Form */}
        <section id="assessment-form" className="py-20 px-6 lg:px-10 bg-[#1a1a1a]">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Left: Info */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-3">Free Assessment</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
                  Get a Free VMware Migration Assessment
                </h2>
                <p className="text-white/60 text-sm leading-relaxed mb-8">
                  Our engineers will audit your current VMware environment, calculate your exact savings potential, and design a zero-downtime migration plan — all at no cost and no commitment.
                </p>
                <div className="space-y-5">
                  {[
                    { icon: "ri-search-eye-line", title: "Environment audit in 1–2 days", desc: "We inventory your VMs, workloads, vSAN usage, and current licensing costs to calculate your exact savings." },
                    { icon: "ri-money-dollar-circle-line", title: "Exact cost comparison", desc: "Side-by-side TCO comparison: your current VMware spend vs Arcfra — with real numbers, not estimates." },
                    { icon: "ri-draft-line", title: "Migration architecture design", desc: "A phased migration plan designed to minimise risk and ensure zero downtime throughout the process." },
                    { icon: "ri-shield-check-line", title: "No commitment required", desc: "The assessment is completely free. You decide whether to proceed after seeing the results." },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-white text-base`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white mb-0.5">{item.title}</p>
                        <p className="text-xs text-white/50 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 pt-8 border-t border-white/10">
                  <p className="text-xs text-white/40 mb-3">Or contact us directly:</p>
                  <div className="flex flex-col gap-2">
                    <a href="tel:+60364127917" className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
                      <i className="ri-phone-line text-white/40"></i>
                      +603 6412 7917
                    </a>
                    <a href="mailto:sales@sedistribution.com.my" className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
                      <i className="ri-mail-line text-white/40"></i>
                      sales@sedistribution.com.my
                    </a>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-white rounded-2xl p-8">
                {assessStatus === "sent" ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 flex items-center justify-center bg-[#f9f7f4] rounded-full mx-auto mb-4">
                      <i className="ri-check-line text-[#1a1a1a] text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">Assessment Request Received!</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      Thank you! Our engineers will contact you within 1 business day to schedule your free VMware environment assessment.
                    </p>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleAssessSubmit} className="space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-[#1a1a1a] mb-1">Free Migration Assessment</h3>
                      <p className="text-xs text-gray-400">No commitment. Completed within 2 business days.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={assessForm.name}
                          onChange={(e) => setAssessForm({ ...assessForm, name: e.target.value })}
                          placeholder="Your name"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Company *</label>
                        <input
                          type="text"
                          name="company"
                          required
                          value={assessForm.company}
                          onChange={(e) => setAssessForm({ ...assessForm, company: e.target.value })}
                          placeholder="Company name"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Email *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={assessForm.email}
                          onChange={(e) => setAssessForm({ ...assessForm, email: e.target.value })}
                          placeholder="your@email.com"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Phone *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={assessForm.phone}
                          onChange={(e) => setAssessForm({ ...assessForm, phone: e.target.value })}
                          placeholder="+60 12 345 6789"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Number of VMs</label>
                        <select
                          name="vmCount"
                          value={assessForm.vmCount}
                          onChange={(e) => setAssessForm({ ...assessForm, vmCount: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer bg-white"
                        >
                          <option value="">Select range</option>
                          <option value="1-20">1–20 VMs</option>
                          <option value="21-50">21–50 VMs</option>
                          <option value="51-100">51–100 VMs</option>
                          <option value="101-250">101–250 VMs</option>
                          <option value="250+">250+ VMs</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Current VMware License</label>
                        <select
                          name="currentLicense"
                          value={assessForm.currentLicense}
                          onChange={(e) => setAssessForm({ ...assessForm, currentLicense: e.target.value })}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer bg-white"
                        >
                          <option value="">Select license</option>
                          <option value="vSphere Standard">vSphere Standard</option>
                          <option value="vSphere Enterprise Plus">vSphere Enterprise Plus</option>
                          <option value="vSphere + vSAN">vSphere + vSAN</option>
                          <option value="VMware Cloud Foundation">VMware Cloud Foundation</option>
                          <option value="Not sure">Not sure</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Tell us about your environment</label>
                      <textarea
                        name="message"
                        value={assessForm.message}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) setAssessForm({ ...assessForm, message: e.target.value });
                        }}
                        placeholder="Current VMware setup, renewal timeline, key workloads, any specific concerns..."
                        rows={3}
                        maxLength={500}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors resize-none"
                      />
                      <p className="text-[10px] text-gray-400 mt-1 text-right">{assessForm.message.length}/500</p>
                    </div>
                    <button
                      type="submit"
                      disabled={assessStatus === "sending"}
                      className="w-full bg-[#1a1a1a] text-white font-semibold py-3.5 rounded-lg hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-60"
                    >
                      {assessStatus === "sending" ? "Sending..." : "Get Free Assessment"}
                    </button>
                    {assessStatus === "error" && (
                      <p className="text-xs text-red-500 text-center">Something went wrong. Please try again or call us directly.</p>
                    )}
                    <p className="text-[10px] text-gray-400 text-center">No commitment required · Response within 1 business day</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-6 lg:px-10 bg-[#f9f7f4]">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">FAQ</span>
              <h2 className="text-3xl font-bold text-[#1a1a1a] mb-4">
                VMware Alternative Malaysia — Common Questions
              </h2>
            </div>
            <div className="space-y-4">
              {faqSchema.mainEntity.map((faq, i) => (
                <details key={i} className="bg-white rounded-xl border border-gray-100 group">
                  <summary className="flex items-center justify-between px-6 py-5 cursor-pointer list-none">
                    <h4 className="font-semibold text-[#1a1a1a] text-sm pr-4">{faq.name}</h4>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-add-line text-gray-400 group-open:hidden"></i>
                      <i className="ri-subtract-line text-gray-400 hidden group-open:block"></i>
                    </div>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-gray-500 leading-relaxed">{faq.acceptedAnswer.text}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-24 bg-[#1a1a1a] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=enterprise%20IT%20team%20server%20room%20data%20center%20infrastructure%20modern%20professional%20environment%2C%20dramatic%20cinematic%20lighting%2C%20dark%20moody%20atmosphere%2C%20wide%20shot%2C%20photorealistic&width=1400&height=600&seq=vmware-alt-cta&orientation=landscape"
            alt="VMware Alternative Malaysia — Get Started"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/80 to-[#1a1a1a]/95"></div>
          <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-map-pin-line text-white/60 text-xs"></i>
              <span className="text-xs text-white/70">Kuala Lumpur, Malaysia</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
              Ready to Escape VMware?
            </h2>
            <p className="text-white/60 text-base max-w-xl mx-auto mb-4 leading-relaxed">
              Get a free VMware environment assessment from SE Distribution's engineers. We'll calculate your exact savings, design your migration architecture, and handle the entire process — with zero downtime.
            </p>
            <p className="text-white/40 text-sm mb-10">
              No commitment required. Assessment completed within 2 business days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#assessment-form"
                className="bg-white text-[#1a1a1a] text-sm font-bold px-10 py-4 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
              >
                Get Free Assessment
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="tel:+60364127917"
                className="border border-white/40 text-white text-sm font-medium px-10 py-4 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
              >
                <i className="ri-phone-line"></i>
                +603 6412 7917
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs text-white/35">
              <span className="flex items-center gap-1.5"><i className="ri-shield-check-line"></i> Authorized Arcfra Distributor</span>
              <span className="flex items-center gap-1.5"><i className="ri-map-pin-line"></i> Kuala Lumpur Office</span>
              <span className="flex items-center gap-1.5"><i className="ri-time-line"></i> Mon–Fri 9:30am–6:30pm</span>
              <span className="flex items-center gap-1.5"><i className="ri-mail-line"></i> sales@sedistribution.com.my</span>
            </div>
          </div>
        </section>

      </main>
      <Footer />
      <StickyQuoteButton formId="assessment-form" label="Get Free Assessment" />
    </div>
  );
}
